import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';

interface GlobalInputFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  autoComplete?: string;
  maxLength?: number;
}

export const GlobalInputField: React.FC<GlobalInputFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  type = 'text',
  required = false,
  disabled = false,
  error = '',
  icon,
  fullWidth = true,
  autoComplete,
  maxLength,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: error ? '#dc3545' : '#0f3d3e',
          marginBottom: '6px',
          letterSpacing: '0.2px'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '1rem',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: 0.5
            }}
          ></i>
        )}
        
        <InputText
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          maxLength={maxLength}
          style={{
            width: '100%',
            padding: icon ? '12px 14px 12px 42px' : isPasswordField ? '12px 42px 12px 14px' : '12px 14px',
            border: `1px solid ${error ? '#dc3545' : '#e5e7eb'}`,
            borderRadius: '10px',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease',
            background: disabled ? '#f8f9fa' : 'white',
            color: disabled ? '#999' : '#333',
            fontWeight: 500
          }}
          className="global-input-field"
        />

        {isPasswordField && (
          <i 
            className={showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'} 
            onClick={() => !disabled && setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '1rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              zIndex: 1,
              transition: 'color 0.2s ease',
              opacity: disabled ? 0.3 : 0.5
            }}
            onMouseEnter={(e) => !disabled && (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => !disabled && (e.currentTarget.style.opacity = '0.5')}
          ></i>
        )}
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#dc3545',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-input-field:enabled:focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 3px rgba(15, 61, 62, 0.1) !important;
        }

        .global-input-field:enabled:hover {
          border-color: #1a5555 !important;
        }

        .global-input-field:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .global-input-field::placeholder {
          color: #999;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

// Demo Component
const GlobalInputFieldDemo = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    aadhar: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: formData.name ? '' : 'Name is required',
      email: formData.email.includes('@') ? '' : 'Valid email is required',
      password: formData.password.length >= 6 ? '' : 'Password must be at least 6 characters'
    };
    setErrors(newErrors);
  };

  return (
    <div style={{ padding: '0', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(212, 241, 126, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            padding: '5px 12px',
            background: 'rgba(212, 241, 126, 0.2)',
            borderRadius: '20px',
            marginBottom: '10px',
            fontSize: '0.7rem',
            fontWeight: '600',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            Component Demo
          </div>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '1.7rem',
            fontWeight: '700',
            color: 'white',
            letterSpacing: '-0.5px'
          }}>
            Global Input Field
          </h1>
          <p style={{
            margin: '0',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.9rem'
          }}>
            Reusable input component with consistent styling
          </p>
        </div>
      </div>

      {/* Demo Form */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="pi pi-user" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
          </div>
          <h2 style={{
            margin: 0,
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#0f3d3e'
          }}>
            Example Form
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <GlobalInputField
            label="Full Name"
            value={formData.name}
            onChange={handleChange('name')}
            placeholder="Enter your full name"
            icon="pi pi-user"
            required
            error={errors.name}
          />

          <GlobalInputField
            label="Email Address"
            value={formData.email}
            onChange={handleChange('email')}
            placeholder="example@email.com"
            type="email"
            icon="pi pi-envelope"
            required
            error={errors.email}
            autoComplete="email"
          />

          <GlobalInputField
            label="Password"
            value={formData.password}
            onChange={handleChange('password')}
            placeholder="Enter password"
            type="password"
            required
            error={errors.password}
            autoComplete="new-password"
          />

          <GlobalInputField
            label="Phone Number"
            value={formData.phone}
            onChange={handleChange('phone')}
            placeholder="Enter 10-digit phone number"
            type="tel"
            icon="pi pi-phone"
          />

          <GlobalInputField
            label="Aadhar Number"
            value={formData.aadhar}
            onChange={handleChange('aadhar')}
            placeholder="Enter 12-digit Aadhar number"
            icon="pi pi-id-card"
          />

          <GlobalInputField
            label="Disabled Field"
            value="This field is disabled"
            onChange={() => {}}
            disabled
            icon="pi pi-lock"
          />
        </div>

        <div style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '2px solid #f0f0f0',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={() => {
              setFormData({
                name: '',
                email: '',
                password: '',
                phone: '',
                aadhar: ''
              });
              setErrors({ name: '', email: '', password: '' });
            }}
            style={{
              padding: '12px 24px',
              background: 'white',
              border: '1px solid #0f3d3e',
              borderRadius: '10px',
              color: '#0f3d3e',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Clear
          </button>
          <button
            onClick={validateForm}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Validate
          </button>
        </div>
      </div>

      {/* Feature List */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginTop: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '1rem',
          fontWeight: '700',
          color: '#0f3d3e'
        }}>
          Features
        </h3>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          color: '#333',
          fontSize: '0.85rem',
          lineHeight: '1.8'
        }}>
          <li>Custom teal/lime color scheme from your design system</li>
          <li>Password visibility toggle with eye icon</li>
          <li>Optional icon support on the left</li>
          <li>Error state with validation messages</li>
          <li>Disabled state styling</li>
          <li>Focus and hover states</li>
          <li>Required field indicator</li>
          <li>Smooth transitions and animations</li>
          <li>Fully responsive and accessible</li>
        </ul>
      </div>
    </div>
  );
};

export default GlobalInputFieldDemo;